<app-navbar></app-navbar>
<picture class="background-plane-picture">
  <source srcset="./assets/background.webp" type="image/webp" />
  <img src="./assets/background.jpg" class="background-plane" alt="" decoding="async" fetchpriority="low" />
</picture>
<div class="new-test-page">
  <div class="new-test-container">
    <!-- Page header -->
    <div class="new-test-header">
      <a routerLink="/dashboard" class="back-link"><i class="bi bi-arrow-left"></i> Back to Dashboard</a>
      <h1 class="new-test-title">Create New Test</h1>
      <p class="new-test-subtitle">Configure your practice test parameters</p>
    </div>

    <form [formGroup]="questionPoolForm" (ngSubmit)="onSubmit()">
      <!-- Question Category panel -->
      <div class="panel new-test-panel">
        <label class="panel-label">Question Category</label>
        <ngx-select-dropdown id="category" class="new-test-dropdown dropdown-standard"
          [options]="categoryList"
          [multiple]="false"
          [config]="{
            displayKey:'name',
            search: true,
            placeholder: 'Please, select a category',
            height: '300px'
          }"
          (change)="onChangeCategory($event)"
          [(ngModel)]="selectedCategory"
          [ngModelOptions]="{ standalone: true }">
        </ngx-select-dropdown>
      </div>

      <!-- Test Mode panel -->
      <div class="panel new-test-panel">
        <label class="panel-label">Test Mode</label>
        <div class="test-mode-toggle" role="group" aria-label="Test mode selection">
          <input type="radio" class="test-mode-radio" name="testMode" id="studyMode"
            [checked]="testMode === 'study'"
            (click)="testMode = 'study'; onModeChange()">
          <label class="test-mode-btn" [class.test-mode-active]="testMode === 'study'" for="studyMode">Study</label>

          <input type="radio" class="test-mode-radio" name="testMode" id="examMode"
            [checked]="testMode === 'exam'"
            (click)="testMode = 'exam'; onModeChange()">
          <label class="test-mode-btn" [class.test-mode-active]="testMode === 'exam'" for="examMode">Exam</label>
        </div>
      </div>

      <!-- Subjects panel -->
      <div *ngIf="subjectLoader" class="panel new-test-panel">
        <div class="panel-loading">
          <strong role="status">Loading subjects...</strong>
          <div class="spinner-border ms-auto" aria-hidden="true"></div>
        </div>
      </div>
      <div *ngIf="!subjectLoader && showSubjects" class="panel new-test-panel">
        <label class="panel-label">Subjects</label>
        <ngx-select-dropdown id="subject" class="new-test-dropdown dropdown-standard"
          [options]="subjectList"
          [multiple]="testMode !== 'exam'"
          [(ngModel)]="selectedSubjectObjects"
          [ngModelOptions]="{ standalone: true }"
          [config]="{
            displayKey:'name',
            search: true,
            placeholder: testMode === 'exam' ? 'Please, select a subject (one only)' : 'Please, select a subject',
            height: '300px',
            enableSelectAll: testMode !== 'exam'
          }"
          (change)="onChangeSubject($event)">
        </ngx-select-dropdown>
      </div>

      <!-- Filters panel - preserve existing filter handling -->
      <div *ngIf="(Array.isArray(selectedSubjectObjects) && selectedSubjectObjects.length > 0) || (selectedSubjectObjects && !Array.isArray(selectedSubjectObjects))" class="panel new-test-panel">
        <label class="panel-label">Filters</label>
        <div class="filters-content">
          <!-- Study mode only: Only unseen questions -->
          <div *ngIf="testMode === 'study'" class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="onlyUnseen"
              [(ngModel)]="onlyUnseen" [ngModelOptions]="{standalone: true}"
              (change)="onOnlyUnseenChange($event)"
              title="Use only questions you have never seen in any test">
            <label class="form-check-label" for="onlyUnseen">Only unseen questions</label>
          </div>

          <!-- Color filters (study only) -->
          <div *ngIf="testMode !== 'exam'" data-testid="color-filters" class="color-filters-row">
            <div class="d-flex align-items-center mb-2">
              <button type="button" class="btn btn-sm p-1 color-filter-btn"
                data-testid="color-filter-red"
                [ngClass]="{'btn-danger': redFlagMode !== 'none', 'btn-outline-danger': redFlagMode === 'none'}"
                (click)="toggleFlagMode('red')" [title]="'Current: ' + getFlagLabel('red')">
                <i class="bi bi-flag-fill"></i>
              </button>
              <small class="ms-2 text-muted color-filter-label">{{ getFlagLabel('red') }}</small>
            </div>
            <div class="d-flex align-items-center mb-2">
              <button type="button" class="btn btn-sm p-1 color-filter-btn"
                data-testid="color-filter-yellow"
                [ngClass]="{'btn-warning': yellowFlagMode !== 'none', 'btn-outline-warning': yellowFlagMode === 'none'}"
                (click)="toggleFlagMode('yellow')" [title]="'Current: ' + getFlagLabel('yellow')">
                <i class="bi bi-flag-fill"></i>
              </button>
              <small class="ms-2 text-muted color-filter-label">{{ getFlagLabel('yellow') }}</small>
            </div>
            <div class="d-flex align-items-center mb-2">
              <button type="button" class="btn btn-sm p-1 color-filter-btn"
                data-testid="color-filter-green"
                [ngClass]="{'btn-success': greenFlagMode !== 'none', 'btn-outline-success': greenFlagMode === 'none'}"
                (click)="toggleFlagMode('green')" [title]="'Current: ' + getFlagLabel('green')">
                <i class="bi bi-flag-fill"></i>
              </button>
              <small class="ms-2 text-muted color-filter-label">{{ getFlagLabel('green') }}</small>
            </div>
          </div>

          <!-- Exclude Hidden -->
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="excludeHidden"
              [(ngModel)]="excludeHidden" [ngModelOptions]="{standalone: true}"
              (change)="onFilterChange()" title="Don't use hidden questions">
            <label class="form-check-label" for="excludeHidden">
              <i class="bi bi-eye-slash-fill me-1"></i>Don't use hidden questions
            </label>
          </div>

          <!-- Study mode only: Wrongly answered -->
          <div *ngIf="testMode === 'study'" class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="onlyWrong"
              [(ngModel)]="onlyWrong" [ngModelOptions]="{standalone: true}"
              (change)="onOnlyWrongChange($event)"
              title="Use only questions you have answered incorrectly in past tests">
            <label class="form-check-label" for="onlyWrong">Wrongly answered</label>
          </div>
        </div>
      </div>

      <!-- Topics panel (Study mode only) -->
      <div *ngIf="testMode === 'study' && Array.isArray(selectedSubjectObjects) && selectedSubjectObjects.length > 0"
        class="panel new-test-panel" data-testid="topic-filter">
        <label class="panel-label">Topics</label>
        <div *ngIf="topicLoader" class="panel-loading">
          <strong role="status">Loading topics...</strong>
          <div class="spinner-border spinner-border-sm ms-auto" aria-hidden="true"></div>
        </div>
        <div *ngIf="topicLoadError" class="alert alert-warning mb-2">{{ topicLoadError }}</div>
        <div *ngIf="!topicLoader">
          <div *ngFor="let subj of selectedSubjectObjects" class="mb-3">
            <div class="topic-subject-block">
              <div class="d-flex align-items-center justify-content-between mb-2" [attr.data-testid]="'subject-topic-toggle-' + subj.id">
                <div class="form-check mb-0 d-flex">
                  <input *ngIf="(topicsBySubjectId[subj.id]?.length || 0) > 0" class="form-check-input" type="checkbox"
                    [id]="'subject_topics_' + subj.id" [checked]="isSubjectTopicsChecked(subj.id)"
                    [indeterminate]="isSubjectTopicsIndeterminate(subj.id)"
                    (change)="onSubjectTopicsToggle(subj.id, $event)"
                    [attr.data-testid]="'subject-checkbox-' + subj.id" />
                  <label class="form-check-label fw-semibold mb-0"
                    [for]="topicsBySubjectId[subj.id].length > 0 ? 'subject_topics_' + subj.id : null">{{ subj.name }}</label>
                </div>
                <button type="button" class="btn btn-sm btn-link p-0 text-decoration-none subject-expand-btn"
                  (click)="toggleSubjectExpanded(subj.id)" [attr.aria-expanded]="isSubjectExpanded(subj.id)"
                  [attr.data-testid]="'subject-expand-' + subj.id" title="Show/hide topics">
                  <span aria-hidden="true">{{ isSubjectExpanded(subj.id) ? '▾' : '▸' }}</span>
                </button>
              </div>
              <div *ngIf="!(topicsBySubjectId[subj.id]?.length) && isSubjectExpanded(subj.id) && topicsBySubjectId[subj.id] !== undefined"
                class="ms-3 text-muted small">No topics for this subject.</div>
              <div *ngIf="isSubjectExpanded(subj.id) && (topicsBySubjectId[subj.id]?.length || 0) > 0" class="ms-3"
                [attr.data-testid]="'subject-topics-' + subj.id">
                <div *ngFor="let t of topicsBySubjectId[subj.id]" class="form-check">
                    <div class="d-flex gap-2 w-100 topic-row-flex" [attr.data-testid]="'topic-row-' + t.id">
                    <input class="form-check-input" type="checkbox" [id]="'topic_' + t.id"
                      [checked]="isTopicSelected(t.id)" (change)="onTopicToggle(t.id, $event)"
                      [attr.data-testid]="'topic-checkbox-' + t.id" />
                    <label class="form-check-label flex-grow-1 mb-0" [for]="'topic_' + t.id">{{ t.name }}</label>
                    <span class="text-muted text-end topic-count" [attr.data-testid]="'topic-count-' + t.id">
                      {{ topicCountsLoader ? '…' : getTopicCount(t.id) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Number of Questions panel -->
      <div *ngIf="(Array.isArray(selectedSubjectObjects) && selectedSubjectObjects.length > 0) || (selectedSubjectObjects && !Array.isArray(selectedSubjectObjects))"
        class="panel new-test-panel">
        <label class="panel-label">Number of Questions</label>
        <p class="questions-available" data-testid="available-questions" [attr.data-slider-max]="sliderOptions.ceil">
          Available: {{ questionsCount }} questions
        </p>
        <ngx-slider [(value)]="amountQuestions" [options]="sliderOptions"
          (valueChange)="onAmountQuestionsChange($event)"></ngx-slider>
        <p class="questions-selected" data-testid="selected-questions">Selected: {{ getValidAmountQuestions() }} questions</p>
      </div>

      <!-- Start Test button -->
      <div class="new-test-actions">
        <button (click)="startTest($event)" class="btn btn-start-test"
          [disabled]="!enableButtons || questionsCount === 0">
          Start Test
        </button>
      </div>
    </form>
  </div>
</div>
